import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { toRpcFilters } from '@/lib/filters/rpc';
import type { ContactLike } from '@/lib/email/oneoff-merge';

export type ToRecipient =
  | { kind: 'contact'; contact: ContactLike }
  | { kind: 'raw'; email: string }
  | null;

export interface ToContactPickerProps {
  value: ToRecipient;
  onChange: (next: ToRecipient) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function displayLabel(value: NonNullable<ToRecipient>): string {
  if (value.kind === 'raw') return value.email;
  const c = value.contact;
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} <${c.primary_email ?? ''}>` : (c.primary_email ?? '');
}

export default function ToContactPicker({ value, onChange }: ToContactPickerProps) {
  const { currentWorkspace } = useWorkspaces();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ContactLike[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (value) return; // recipient set: no search
    const q = query.trim();
    if (q.length < 2 || !currentWorkspace?.id) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const { data } = await supabase.rpc('filter_contacts', {
        p_workspace_id: currentWorkspace.id,
        filters: toRpcFilters({ search: q }),
        page_size: 15,
        page_number: 1,
        count_only: false,
        sort_config: null,
      });
      const rows = ((data as Array<ContactLike & { total_count?: number }>) ?? []).map(
        ({ total_count, ...rest }) => rest as ContactLike,
      );
      setResults(rows);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, currentWorkspace?.id, value]);

  if (value) {
    return (
      <div className="flex items-center">
        <Badge variant="secondary" className="flex items-center gap-1 py-1">
          <span className="truncate max-w-[260px]">{displayLabel(value)}</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Remove recipient"
            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
            onClick={() => onChange(null)}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        type="email"
        placeholder="Recipient"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const q = query.trim();
            if (EMAIL_RE.test(q)) {
              onChange({ kind: 'raw', email: q });
              setQuery('');
              setResults([]);
            }
          }
        }}
        className="border-input/60 focus:border-primary"
      />
      {(results.length > 0 || (query.trim().length >= 2)) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
              onClick={() => {
                onChange({ kind: 'contact', contact: c });
                setQuery('');
                setResults([]);
              }}
            >
              <div className="font-medium">
                {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.primary_email}
              </div>
              <div className="text-xs text-muted-foreground">{c.primary_email}</div>
            </button>
          ))}
          {results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No match — press Enter to use this address
            </div>
          )}
        </div>
      )}
    </div>
  );
}
